// broadcast basic analytics directive
//
//   <broadcast-basic-analytics broadcast="vm.broadcast" />
import {
  normalizeOSs,
  normalizePlatform,
  normalizeHostData,
  computeInsights,
} from '../utils/analytics';

let module = angular.module('app.directives');

module.component('broadcastBasicAnalytics', {
  bindings: {
    broadcast: '<',
  },
  template: require('./broadcast-basic-analytics.html'),
  controllerAs: 'vm',
  controller: function ($scope, $rootScope, $timeout, AnalyticsResource) {
    'ngInject';
    this.hasAnalyticsFeature = !!$rootScope.account.feature_flags.analytics;
    this.canReadViewerAnalyticsReports = !!$rootScope.user.can('read', 'viewer-analytics/*');

    this.scheduleNextReload = () => {
      switch (this.broadcast.timeframe) {
        case 'future': // fall-through
        case 'preroll':
          // If the broadcast hasn't started yet, reload at the start time (just in case they keep this tab open forever)
          let msUntilStart = moment().valueOf() - moment(this.broadcast.starts_at).valueOf();
          $timeout.cancel(this._reloadHandle);
          this._reloadHandle = $timeout(() => this.reloadAnalytics(), msUntilStart);
          break;

        case 'current':
          // If we've already loaded analytics once, use the refresh interval; otherwise load once right away
          let msUntilLoad = this.analytics ? REFRESH_INTERVAL_MS : 0;
          $timeout.cancel(this._reloadHandle);
          this._reloadHandle = $timeout(() => this.reloadAnalytics(), msUntilLoad);
          break;

        case 'past':
          // Only load the first time
          if (!this.analytics) {
            // But if there's an error, delay the retry for the refresh interval
            let msUntilLoad = this.error ? REFRESH_INTERVAL_MS : 0;
            $timeout.cancel(this._reloadHandle);
            this._reloadHandle = $timeout(() => this.reloadAnalytics(), msUntilLoad);
          } else {
            this._reloadHandle = null;
          }
          break;
      }
    };

    this.reloadAnalytics = () => {
      AnalyticsResource.get({
        channel_id: this.broadcast.channel_id,
        since: moment(this.broadcast.starts_at)
          .local()
          .set({ h: 0, m: 0, s: 0, ms: 0 })
          .format('YYYY-MM-DDTHH:mm:ssZ'),
        until: moment()
          .add(1, 'day')
          .local()
          .set({ h: 0, m: 0, s: 0, ms: 0 })
          .format('YYYY-MM-DDTHH:mm:ssZ'),
        include_viewer_timeseries: true,
      })
        .$promise.then(
          (a) => {
            this.analytics = a;
            this.postProcessAnalytics();
          },
          (e) => {
            console.error(e);
            this.error = e;
          }
        )
        .finally(() => {
          this.loading = false;
          this.scheduleNextReload();
        });
    };

    this.hasViews = () => {
      return this.broadcast.total_view_count > 0;
    };

    this.postProcessAnalytics = () => {
      // glue on computed fields
      this.analytics.total_view_count =
        this.analytics.live_view_count + this.analytics.recorded_view_count;
      this.analytics.total_view_duration =
        this.analytics.live_view_seconds + this.analytics.recorded_view_seconds;
      this.analytics.avg_view_duration =
        this.analytics.total_view_duration / this.analytics.total_view_count;
      if (this.analytics.live_buffer_seconds == 0 || this.analytics.live_view_seconds == 0) {
        this.analytics.live_rebuffer_pct = 0;
      } else {
        this.analytics.live_rebuffer_pct =
          100 *
          (this.analytics.live_buffer_seconds /
            (this.analytics.live_buffer_seconds + this.analytics.live_view_seconds));
      }
      if (
        this.analytics.recorded_buffer_seconds == 0 ||
        this.analytics.recorded_view_seconds == 0
      ) {
        this.analytics.recorded_rebuffer_pct = 0;
      } else {
        this.analytics.recorded_rebuffer_pct =
          100 *
          (this.analytics.recorded_buffer_seconds /
            (this.analytics.recorded_buffer_seconds + this.analytics.recorded_view_seconds));
      }

      // Better formatting on hosts
      this.analytics.hostData = normalizeHostData(this.analytics);

      this.insights = computeInsights(this.broadcast, this.analytics);
      this.analytics.view_counts_by_os = normalizeOSs(this.analytics.view_counts_by_os);

      // Viewer engagement graph
      this.viewsLabels = _.map(this.analytics.active_viewer_log, (l) => moment(l[0] * 1000));
      this.viewsData = [_.map(this.analytics.active_viewer_log, (l) => l[1])];
      if (this.viewsLabels.length) {
        if (this.viewsLabels[0].toDate() > moment(this.broadcast.starts_at).toDate()) {
          // Insert an extra datapoint at exact beginning if it's not already there
          this.viewsLabels.splice(0, 0, moment(this.broadcast.starts_at));
          this.viewsData[0].splice(0, 0, 0);
        }
        while (_.last(this.viewsLabels).toDate() > moment(this.broadcast.stops_at).toDate()) {
          // Kill off datapoints after end of broadcast
          this.viewsLabels.splice(this.viewsLabels.length - 1, 1);
          this.viewsData[0].splice(this.viewsData[0].length - 1, 1);
        }
        if (this.broadcast.timeframe == 'current') {
          if (moment().toDate() > _.last(this.viewsLabels).toDate()) {
            // Insert an extra datapoint at exact *now* to stretch the line
            this.viewsLabels.push(moment());
            this.viewsData[0].push(this.broadcast.total_active_viewer_count);
          }
          // Insert an extra NaN datapoint at end of broadcast (draws no line but keeps x-axis
          this.viewsLabels.push(moment(this.broadcast.stops_at));
          this.viewsData[0].push(null);
        } else {
          if (_.last(this.viewsLabels).toDate() < moment(this.broadcast.stops_at).toDate()) {
            // Insert an extra datapoint at exact end if it's not already there
            this.viewsLabels.push(moment(this.broadcast.stops_at));
            this.viewsData[0].push(0);
          }
        }
      }
    };

    this.viewsOptions = {
      lineTension: 0,
      legend: { display: false },
      scales: {
        yAxes: [
          {
            gridLines: {
              color: '#FFF',
              zeroLineColor: '#FFF',
              drawTicks: false,
              drawBorder: false,
              tickMarkLength: 0,
            },
            ticks: {
              suggestedMax: 10,
              beginAtZero: true,
              callback: function (value, index, values) {
                return Math.round(value);
              },
              fontFamily: 'Arial, sans-serif',
              fontSize: 11,
            },
            stacked: true,
          },
        ],
        xAxes: [
          {
            type: 'time',
            time: { unit: 'minute' },
            gridLines: { display: false },
            position: 'bottom',
            ticks: {
              fontSize: 11,
              fontFamily: 'Arial, sans-serif',
              autoSkip: true,
            },
          },
        ],
      },
      tooltips: {
        callbacks: {
          //title: (items, data) => {
          //  return this.viewsLabels[items[0].index].format('ddd MMM Do');
          //}
        },
      },
      animation: {
        duration: 0,
      },
    };

    this.datasetOverride = [
      { pointRadius: 0, pointHitRadius: 5, pointHoverRadius: 4, lineTension: 0, steppedLine: true },
    ];

    // Load full analytics data
    if (this.hasAnalyticsFeature) {
      // Only set loading = true the first time to show the loading panel. On refresh, we already have data to show.
      this.loading = true;
      this.scheduleNextReload();
      $scope.$on('$destroy', () => $timeout.cancel(this._reloadHandle));
    }
  },
});



// WEBPACK FOOTER //
// ./app/directives/broadcast-basic-analytics.js